import React, { useState } from 'react';
import { Mail, CheckCircle2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * NewsletterSignup, opt-in email capture for the 5-part audit-readiness
 * email course. Used on /guides, guide articles and the checklist page.
 * Posts to /api/newsletter/subscribe. Explicitly opt-in (GDPR).
 */
export const NewsletterSignup = ({ source = 'guides', dark = false }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [message, setMessage] = useState('');

  const ink = dark ? '#fff' : '#0A2540';
  const sub = dark ? '#CBD5E1' : '#5B6B82';
  const bg = dark ? 'rgba(255,255,255,0.06)' : '#fff';
  const border = dark ? 'rgba(255,255,255,0.15)' : '#E5E1D8';

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }
    setStatus('sending');
    try {
      const res = await fetch(`${API}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setStatus('done');
      } else {
        setStatus('error');
        setMessage(data.detail || 'Something went wrong, please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Network error, please try again.');
    }
  };

  if (status === 'done') {
    return (
      <div data-testid="newsletter-success" style={{ background: bg, border: `1px solid ${border}`, borderLeft: '3px solid #16A34A', borderRadius: 6, padding: '26px 28px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <CheckCircle2 size={22} color="#16A34A" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: ink }}>You're in, check your inbox.</div>
            <div style={{ fontSize: 13.5, color: sub, marginTop: 6, lineHeight: 1.6 }}>
              Email 1 of the audit-readiness course (with the checklist PDF) is on its way. Five short emails over two weeks, unsubscribe with one click anytime.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="newsletter-signup" style={{ background: bg, border: `1px solid ${border}`, borderLeft: '3px solid #D6336C', borderRadius: 6, padding: '26px 28px' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 6 }}>
        <Mail size={18} color="#D6336C" />
        <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, color: '#D6336C' }}>
          Free 5-part email course
        </div>
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, color: ink, lineHeight: 1.3 }}>
        Get audit-ready in five short emails.
      </div>
      <div style={{ fontSize: 13.5, color: sub, marginTop: 6, lineHeight: 1.6, maxWidth: 560 }}>
        The 18-point checklist PDF, the 30-second traceability test, what BRCGS auditors actually flag, and how Cashel Blue got audit-ready, one email every few days. No spam, one-click unsubscribe.
      </div>
      <form onSubmit={submit} noValidate style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
        <input
          data-testid="newsletter-email-input"
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
          placeholder="you@yourcompany.ie"
          style={{
            flex: '1 1 220px',
            padding: '12px 16px',
            fontSize: 14,
            border: `1px solid ${border}`,
            borderRadius: 4,
            background: dark ? 'rgba(255,255,255,0.08)' : '#FAF8F5',
            color: ink,
            outline: 'none',
          }}
        />
        <button
          data-testid="newsletter-submit-button"
          type="submit"
          disabled={status === 'sending'}
          style={{
            background: '#D6336C',
            color: '#fff',
            border: 'none',
            padding: '12px 22px',
            fontSize: 14,
            fontWeight: 700,
            borderRadius: 4,
            cursor: status === 'sending' ? 'wait' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            opacity: status === 'sending' ? 0.7 : 1,
          }}
        >
          {status === 'sending' ? 'Sending…' : 'Start the course'} <ArrowRight size={15} />
        </button>
      </form>
      {status === 'error' && (
        <div data-testid="newsletter-error" style={{ fontSize: 13, color: '#DC2626', marginTop: 10 }}>{message}</div>
      )}
      <div style={{ fontSize: 11.5, color: sub, marginTop: 12 }}>
        GDPR-friendly: we store your email for this course only. Unsubscribe link in every email.
      </div>
    </div>
  );
};

export default NewsletterSignup;
